<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Offcanvas, Row } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';
  import { format as formatDate } from 'date-fns';

  import { FilterBar } from '@client/components/filter-bar';
  import { MultiSelectFilter } from '@client/components/filters/multiselect';
  import { SearchFilter } from '@client/components/filters/search';
  import { SelectFilter } from '@client/components/filters/select';
  import { Loader } from '@client/components/loader';
  import { Table } from '@client/components/table';

  import { ReportsService } from '@client/services/reports';

  import { appStore } from '@client/stores/app';

  import { exportTableToCSV } from '@client/utils/csv';
  import { LocalStorage } from '@client/utils/local-storage';
  import { QueryParams } from '@client/utils/query-params';

  import { AUDIT_HISTORY_ACTIONS, AUDIT_HISTORY_FILTERS, AUDIT_HISTORY_TYPES } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  import { ActionCell, DateCell, NotesCell, TypeCell, UserCell } from './cell-formatters';
  import { RowExpansion } from './row-expansion';

  const { basepath } = getContext('config');

  let filters = deepCopy(AUDIT_HISTORY_FILTERS);
  let days = QueryParams.get('days') || LocalStorage.get('auditHistory')?.days || 730;
  let showFilters = true;
  let search = QueryParams.get('search');

  let initialized = false;
  let updating = false;
  let original = [];
  let expanded = [];
  let filtered = [];
  let selectedEntryData = null;
  let showEntryDetails = false;

  Object.keys(filters.timeframe).forEach((key) => {
    filters.timeframe[key].enabled = filters.timeframe[key].value === days;
  });

  const queryFn = async () => {
    updating = true;

    try {
      const response = await ReportsService.getAuditHistory({ days });

      // Normalize audit entries: reclassify add-in updates from 'manual' to 'outlookaddin'
      const normalizedResponse = Array.isArray(response)
        ? response.map((entry) => {
            if (
              entry.action === AUDIT_HISTORY_ACTIONS.updateuser.id &&
              entry.type === AUDIT_HISTORY_TYPES.manual.id &&
              entry.note?.data?.outlookAddinVersion
            ) {
              return { ...entry, type: AUDIT_HISTORY_TYPES.outlookaddin.id };
            }
            return entry;
          })
        : [];

      original = filtered = normalizedResponse;

      updateTableRows();

      return normalizedResponse;
    } catch (error) {
      console.error('Failed to load audit history:', error);
      original = filtered = [];
      return [];
    } finally {
      updating = false;
      initialized = true;
    }
  };

  const query = createQuery({ queryKey: ['audit-history', filters], queryFn });

  let columns = [
    {
      key: 'timestamp',
      title: $format('label.DATE'),
      headerClass: 'width-10',
      value: ({ timestamp }) => timestamp,
      renderComponent: DateCell,
      sortable: true
    },
    {
      key: 'user',
      title: $format('label.USER'),
      headerClass: 'width-30p',
      value: ({ user }) => user,
      renderComponent: UserCell,
      sortable: true
    },
    {
      key: 'action',
      title: $format('label.ACTION'),
      headerClass: 'width-10p',
      value: ({ action }) => AUDIT_HISTORY_ACTIONS[action]?.label || action,
      renderComponent: ActionCell,
      sortable: true
    },
    {
      key: 'type',
      title: $format('label.TYPE'),
      headerClass: 'width-10p',
      value: ({ type }) => AUDIT_HISTORY_TYPES[type]?.label || type,
      renderComponent: TypeCell,
      sortable: true
    },
    {
      key: 'note',
      title: $format('label.SUMMARY'),
      headerClass: 'width-40p',
      renderComponent: NotesCell
    }
  ];

  const exportToCSV = () =>
    exportTableToCSV(
      document.querySelector('#audit-table'),
      `audit-history-${formatDate(new Date(), 'MM-dd-yyyy-h:mm:ss-a')}`
    );

  const updateTableRows = () => {
    const selectedActions = Object.values(filters.actions)
      .map(({ enabled, value }) => enabled && value)
      .filter(Boolean);

    const selectedTypes = Object.values(filters.types)
      .map(({ enabled, value }) => enabled && value)
      .filter(Boolean);

    let rows = [...original];

    if (selectedActions.length) {
      rows = rows.filter(({ action }) => selectedActions.includes(action));
    }

    if (selectedTypes.length) {
      rows = rows.filter(({ type }) => selectedTypes.includes(type));
    }

    if (search.length) {
      rows = rows.filter((row) => {
        const searchValue = search.toLowerCase();

        return Object.values(row).some((field) => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(searchValue);
          }

          return false;
        });
      });
    }

    filtered = [...rows];
  };

  const onRowChange = ({ detail }) => {};

  const updateHistoryTimeframe = (e, totalDays) => {
    days = totalDays;
    LocalStorage.update('auditHistory', { days });
    queryFn();
  };

  const onEntrySelect = (data) => {
    selectedEntryData = data;
    showEntryDetails = true;
  };

  const toggleEntryDetails = () => {
    showEntryDetails = !showEntryDetails;
  };

  $: updateTableRows(filters, search);
</script>

<div id="audit" class="page" class:with-footer={$query.data?.data}>
  <Loader backdrop show={$query.isLoading || $query.isFetching || updating} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.AUDIT_HISTORY')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col sm="9" md="7" xs="6">
          <h1 class="page-title">{$format('label.AUDIT_HISTORY')}</h1>
        </Col>
        <Col sm="3" md="5" xs="6" class="text-right">
          <Button color="secondary" on:click={exportToCSV}>{$format('label.EXPORT')}</Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    {#if initialized}
      <div class="toolbar">
        <FilterBar bind:show={showFilters}>
          <div class="toolbar-right" class:collapsed={!showFilters}>
            <SearchFilter bind:search />
            <SelectFilter
              bind:filter={filters.timeframe}
              param="days"
              maxHeight="400px"
              label={$format('label.TIMEFRAME') + ':'}
              onClick={updateHistoryTimeframe}
              placeholder={$format('label.PLACEHOLDER.SELECT_LOOKBACK')}
            />
            <MultiSelectFilter
              bind:filter={filters.actions}
              param="actions"
              maxHeight="400px"
              search={true}
              label={$format('label.ACTIONS')}
              tooltipLabel={$format('label.SELECTED_ACTIONS')}
            />
            <MultiSelectFilter
              bind:filter={filters.types}
              param="types"
              maxHeight="400px"
              search={true}
              label={$format('label.TYPES')}
              tooltipLabel={$format('label.SELECTED_TYPES')}
            />
          </div>
        </FilterBar>
      </div>

      {#if !$appStore.mobile}
        <Table
          id="audit-table"
          classNameThead="sticky-header"
          classNameRowExpanded="expanded-row"
          classNameExpandedContent="expanded-content"
          expandRowKey="timestamp"
          expandOnRowClick={true}
          showExpandIcon={true}
          expandSingle={true}
          sortBy="timestamp"
          sortOrder={-1}
          emptyLabel={$format('label.MESSAGE.NO_MATCHES_AUDIT_HISTORY')}
          bind:expanded
          bind:rows={filtered}
          bind:columns
        >
          <div slot="expanded" let:row>
            <RowExpansion {row} on:row-updated={onRowChange} />
          </div>
        </Table>
      {:else}
        <div id="audit-rows">
          {#each filtered as row}
            <div class="audit-row" role="button" tabindex="0" on:click={() => onEntrySelect(row)}>
              <div class="details">
                <div>
                  <DateCell {row} />
                  <div>{row.user}</div>
                </div>
                <div class="badges">
                  <ActionCell {row} />
                  <TypeCell {row} />
                </div>
              </div>
              <div>
                <NotesCell {row} />
              </div>
            </div>
          {/each}
        </div>
        <Offcanvas
          class="audit-offcanvas"
          isOpen={showEntryDetails}
          toggle={toggleEntryDetails}
          placement="end"
          style="width: 98%"
        >
          <div slot="header">
            {#if selectedEntryData}
              <DateCell row={selectedEntryData} />
            {/if}
          </div>
          {#if selectedEntryData}
            <RowExpansion row={selectedEntryData} on:row-updated={onRowChange} />
          {/if}
        </Offcanvas>
      {/if}
    {/if}
  </div>
</div>

<style lang="scss" src="./audit-history.scss"></style>
